/* eslint-disable */
import React, { useMemo } from "react";

const MAX_CHARS = 100;

const SUGGESTIONS = [
  "No supporting document provided",
  "Document unclear or incomplete",
  "Document does not match reported absence",
  "Document out of date",
  "Absence does not meet authorised criteria",
  "Please contact the school office",
];

const BLOCKED_TERMS = [
  "useless",
  "worthless",
  "stupid",
  "idiot",
  "lazy",
  "bad parent",
  "poor parent",
  "terrible parent",
  "irresponsible",
  "negligent",
  "neglect",
  "neglected",
  "failure",
  "ruin",
  "your child will",
  "child will",
  "lying",
  "lie",
  "lies",
  "liar",
  "dishonest",
  "fake",
  "fraud",
  "made up",
  "ridiculous",
  "nonsense",
  "pathetic",
  "blame",
  "your fault",
  "because of you",
  "your decision",
  "you decided",
  "you keep",
  "keeps",
  "refuses",
  "refused",
  "punish",
  "punishment",
];

const WARNING_TERMS = [
  "always",
  "never",
  "clearly",
  "obviously",
  "definitely",
  "wrong",
  "not true",
  "false",
  "unacceptable",
  "inappropriate",
  "bad",
  "poor",
  "angry",
  "upset",
  "excuse",
  "excuses",
  "future",
];

const SENSITIVE_TERMS = [
  "hospital",
  "diagnosis",
  "condition",
  "illness",
  "surgery",
  "nhs",
  "mental",
  "depression",
  "anxiety",
  "medication",
  "prescription",
  "disability",
  "disorder",
  "medical",
  "doctor",
  "gp",
  "therapy",
  "counselling",
];

const NEUTRAL_REWRITES = [
  ["you decision will make your child useless in future", "absence does not meet authorised criteria"],
  ["your decision will make your child useless in future", "absence does not meet authorised criteria"],
  ["your child useless", "absence does not meet authorised criteria"],
  ["useless in future", "absence does not meet authorised criteria"],
  ["bad parent", "please contact the school office"],
  ["poor parent", "please contact the school office"],
  ["terrible parent", "please contact the school office"],
  ["you keep", "previous submissions were reviewed"],
  ["keeps sending", "previous submissions were reviewed"],
  ["keeps", "previous submissions were reviewed"],
  ["lies", "information provided could not be verified"],
  ["lie", "information provided could not be verified"],
  ["liar", "information provided could not be verified"],
  ["dishonest", "information provided could not be verified"],
  ["fake", "documentation could not be validated"],
  ["fraud", "documentation could not be validated"],
  ["made up", "insufficient supporting information"],
  ["wrong letter", "document did not meet requirements"],
  ["confusing", "unclear"],
  ["doesn't explain anything", "does not clearly explain the absence"],
  ["does not explain anything", "does not clearly explain the absence"],
];

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function findHits(text, terms) {
  const lower = String(text || "").toLowerCase();
  return terms.filter((term) => {
    const t = String(term || "").toLowerCase().trim();
    if (!t) return false;

    if (t.includes(" ")) return lower.includes(t);

    const regex = new RegExp(`\\b${escapeRegExp(t)}\\b`, "i");
    return regex.test(lower);
  });
}

export function analyseAbsenceDecisionNote(text = "") {
  const value = String(text || "").slice(0, MAX_CHARS);

  const blockedHits = findHits(value, BLOCKED_TERMS);
  const sensitiveHits = findHits(value, SENSITIVE_TERMS);
  const warningHits = findHits(value, WARNING_TERMS);

  let tone = "good";
  if (blockedHits.length || sensitiveHits.length) tone = "blocked";
  else if (warningHits.length) tone = "warning";

  return {
    tone,
    blockedHits,
    sensitiveHits,
    warningHits,
    flags: [...blockedHits, ...sensitiveHits, ...warningHits],
    length: value.length,
    max: MAX_CHARS,
    isBlocked: tone === "blocked",
  };
}

function neutralise(text = "") {
  let next = String(text || "");

  NEUTRAL_REWRITES.forEach(([from, to]) => {
    const pattern = new RegExp(escapeRegExp(from), "gi");
    next = next.replace(pattern, to);
  });

  const analysis = analyseAbsenceDecisionNote(next);

  if (analysis.isBlocked) {
    return "absence does not meet authorised criteria";
  }

  return next.slice(0, MAX_CHARS);
}

export default function AbsenceDecisionNotePanel({
  value = "",
  onChange,
  onToneChange,
}) {
  const safeValue = String(value || "").slice(0, MAX_CHARS);

  const analysis = useMemo(
    () => analyseAbsenceDecisionNote(safeValue),
    [safeValue]
  );

  React.useEffect(() => {
    if (typeof onToneChange === "function") {
      onToneChange(analysis);
    }
  }, [analysis, onToneChange]);

  function setValue(next) {
    if (typeof onChange === "function") {
      onChange(String(next || "").slice(0, MAX_CHARS));
    }
  }

  const toneStyle =
    analysis.tone === "blocked"
      ? styles.toneBlocked
      : analysis.tone === "warning"
      ? styles.toneWarning
      : styles.toneGood;

  return (
    <section style={styles.wrapper}>
      <div style={styles.header}>
        <div>
          <p style={styles.eyebrow}>Writing guidance for absence decisions</p>
          <h3 style={styles.title}>Optional rejection note</h3>
          <p style={styles.subtitle}>
            Keep this short, neutral, and factual. Avoid medical, emotional, or judgemental wording.
          </p>
        </div>

        <span style={{ ...styles.toneBadge, ...toneStyle }}>
          {analysis.tone === "blocked"
            ? "Blocked"
            : analysis.tone === "warning"
            ? "Review"
            : "Good"}
        </span>
      </div>

      <div style={styles.suggestions}>
        {SUGGESTIONS.map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            style={styles.suggestion}
            onClick={() => setValue(suggestion)}
          >
            {suggestion}
          </button>
        ))}
      </div>

      <textarea
        value={safeValue}
        maxLength={MAX_CHARS}
        onChange={(e) => setValue(e.target.value)}
        style={{
          ...styles.textarea,
          ...(analysis.tone === "blocked"
            ? styles.textareaBlocked
            : analysis.tone === "warning"
            ? styles.textareaWarning
            : {}),
        }}
        placeholder="Enter optional internal note..."
      />

      <div style={styles.metaRow}>
        <span
          style={{
            ...styles.counter,
            ...(safeValue.length > 85 ? styles.counterWarning : {}),
          }}
        >
          {safeValue.length}/{MAX_CHARS}
        </span>

        <button
          type="button"
          style={styles.neutralButton}
          onClick={() => setValue(neutralise(safeValue))}
        >
          Apply neutral wording
        </button>
      </div>

      {analysis.tone === "blocked" ? (
        <div style={styles.blockedBox}>
          This wording may be seen as accusatory, judgemental, sensitive, or unsafe.
          Please use neutral, factual language before confirming rejection.
          {analysis.flags.length ? (
            <div style={styles.flagLine}>
              Flagged: {analysis.flags.slice(0, 6).join(", ")}
            </div>
          ) : null}
        </div>
      ) : analysis.tone === "warning" ? (
        <div style={styles.warningBox}>
          Consider using more neutral wording.
          {analysis.warningHits.length ? (
            <div style={styles.flagLine}>
              Flagged: {analysis.warningHits.slice(0, 6).join(", ")}
            </div>
          ) : null}
        </div>
      ) : (
        <div style={styles.helperBox}>
          This note is for internal decision context only.
        </div>
      )}
    </section>
  );
}

const styles = {
  wrapper: {
    background: "#f8fbff",
    border: "1px solid #e5ebf7",
    borderRadius: 20,
    padding: 18,
    display: "grid",
    gap: 14,
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
  },

  eyebrow: {
    margin: 0,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "#64748b",
    fontWeight: 950,
  },

  title: {
    margin: "5px 0 0",
    fontSize: 18,
    fontWeight: 950,
    color: "#0f172a",
  },

  subtitle: {
    margin: "6px 0 0",
    fontSize: 13,
    color: "#64748b",
    lineHeight: 1.45,
  },

  toneBadge: {
    borderRadius: 999,
    padding: "7px 11px",
    fontSize: 12,
    fontWeight: 950,
    color: "#fff",
    whiteSpace: "nowrap",
  },

  toneGood: {
    background: "#16a34a",
  },

  toneWarning: {
    background: "#d97706",
  },

  toneBlocked: {
    background: "#dc2626",
  },

  suggestions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },

  suggestion: {
    border: "1px solid #dbeafe",
    background: "#ffffff",
    color: "#0f2a5f",
    padding: "8px 12px",
    borderRadius: 999,
    fontSize: 12,
    cursor: "pointer",
    fontWeight: 850,
    minHeight: 34,
    boxShadow: "0 8px 18px rgba(30, 64, 175, 0.05)",
  },

  textarea: {
    width: "100%",
    minHeight: 88,
    boxSizing: "border-box",
    borderRadius: 16,
    border: "1px solid #dbeafe",
    background: "#ffffff",
    color: "#0f172a",
    padding: 12,
    fontSize: 14,
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
    lineHeight: 1.5,
  },

  textareaWarning: {
    border: "1px solid #f59e0b",
    background: "#fffbeb",
  },

  textareaBlocked: {
    border: "1px solid #fca5a5",
    background: "#fff5f5",
  },

  metaRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },

  counter: {
    fontSize: 12,
    color: "#64748b",
    fontWeight: 850,
  },

  counterWarning: {
    color: "#b45309",
  },

  neutralButton: {
    border: "1px solid #dbeafe",
    background: "#ffffff",
    color: "#1d4ed8",
    padding: "9px 12px",
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 10px 20px rgba(30, 64, 175, 0.07)",
  },

  blockedBox: {
    padding: 12,
    borderRadius: 14,
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    fontSize: 13,
    fontWeight: 850,
    lineHeight: 1.5,
  },

  warningBox: {
    padding: 12,
    borderRadius: 14,
    background: "#fffbeb",
    border: "1px solid #fde68a",
    color: "#92400e",
    fontSize: 13,
    fontWeight: 850,
    lineHeight: 1.5,
  },

  helperBox: {
    color: "#64748b",
    fontSize: 13,
    fontWeight: 750,
  },

  flagLine: {
    marginTop: 7,
    fontWeight: 950,
  },
};